import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useInvoice } from '@/hooks/useInvoice';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { 
  ArrowLeft, Download, Edit, Printer, CheckCircle2, Clock, XCircle, 
  AlertCircle, Share2, CreditCard, Lock, Eye, Crown, Laptop, 
  Smartphone, ExternalLink 
} from 'lucide-react';
import { exportInvoiceToPDF } from '@/lib/export';
import { exportToWord } from '@/lib/exportUtils';
import { InvoicePrintView } from '@/components/invoice/InvoicePrintView';
import type { Invoice } from '@/types/invoice';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { format } from 'date-fns';

interface DocumentView {
  id: string;
  viewedAt: Date;
  userAgent: string;
  browser: string;
  os: string;
}

export default function InvoicePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getInvoice, incrementDownloadCount } = useInvoice();
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [views, setViews] = useState<DocumentView[]>([]);
  const [loadingViews, setLoadingViews] = useState(false);

  const handleShare = () => {
    if (!invoice || !user) return;
    const shareUrl = `${window.location.origin}/share/invoice/${user.uid}/${invoice.invoiceId}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: 'Link Copied',
      description: 'Shareable invoice link copied to clipboard!',
    });
  };

  useEffect(() => {
    const loadInvoice = async () => {
      if (id) {
        const data = await getInvoice(id);
        setInvoice(data);
        setLoading(false);
      }
    };
    loadInvoice();
  }, [id]);

  useEffect(() => {
    const fetchViews = async () => {
      if (!id || !user || !isPro) return;
      setLoadingViews(true);
      try {
        const viewsRef = collection(db, 'users', user.uid, 'invoices', id, 'views');
        const q = query(viewsRef, orderBy('viewedAt', 'desc'));
        const snapshot = await getDocs(q);
        const viewsList = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            viewedAt: data.viewedAt?.toDate ? data.viewedAt.toDate() : new Date(data.viewedAt),
            userAgent: data.userAgent || '',
            browser: data.browser || '',
            os: data.os || '',
          };
        });
        setViews(viewsList);
      } catch (error) {
        console.error('Error fetching views:', error);
      } finally {
        setLoadingViews(false);
      }
    };

    if (invoice) {
      fetchViews();
    }
  }, [invoice, id, user, isPro]);

  const handlePrint = async () => {
    if (!invoice) return;
    window.print();
    await incrementDownloadCount();
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;

    setDownloading(true);
    try {
      await exportInvoiceToPDF(invoice, 'invoice-content');
      await incrementDownloadCount();
      toast({ title: 'Success', description: 'Invoice downloaded successfully!' });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download invoice. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!invoice) return;

    setDownloadingWord(true);
    try {
      const filename = `${invoice.invoiceNumber || 'invoice'}.docx`;
      await exportToWord(invoice, filename);
      await incrementDownloadCount();
      toast({ title: 'Success', description: 'Word document downloaded successfully!' });
    } catch (error) {
      console.error('Download Word error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download Word document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingWord(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { icon: any; color: string }> = {
      paid:      { icon: CheckCircle2, color: 'bg-green-100 text-green-800 border-green-200' },
      sent:      { icon: Clock,        color: 'bg-blue-100 text-blue-800 border-blue-200' },
      draft:     { icon: AlertCircle,  color: 'bg-gray-100 text-gray-800 border-gray-200' },
      overdue:   { icon: XCircle,      color: 'bg-red-100 text-red-800 border-red-200' },
      cancelled: { icon: XCircle,      color: 'bg-red-100 text-red-800 border-red-200' },
    };
    const statusConfig = config[status] || config.draft;
    const Icon = statusConfig.icon;
    return (
      <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit border`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-16 text-center">
            <h3 className="text-xl font-semibold mb-2">Invoice not found</h3>
            <p className="text-muted-foreground mb-4">
              The invoice you're looking for doesn't exist or has been deleted.
            </p>
            <Link to="/invoices"><Button>Back to Invoices</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Action Bar — hidden when printing */}
      <div className="bg-white border-b print:hidden">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/invoices')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              {getStatusBadge(invoice.status)}
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => navigate(`/invoices/edit/${id}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              {!isPro ? (
                <Button variant="outline" disabled className="opacity-60 cursor-not-allowed">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Link
                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200">PRO</Badge>
                </Button>
              ) : (
                <Button variant="outline" onClick={handleShare}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Link
                </Button>
              )}
              {!isPro ? (
                <Button variant="outline" disabled className="opacity-60 cursor-not-allowed">
                  <Download className="h-4 w-4 mr-2" />
                  Download Word
                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200">PRO</Badge>
                </Button>
              ) : (
                <Button variant="outline" onClick={handleDownloadWord} disabled={downloadingWord}>
                  <Download className="h-4 w-4 mr-2" />
                  {downloadingWord ? 'Downloading...' : 'Download Word'}
                </Button>
              )}
              <Button onClick={handleDownloadPDF} disabled={downloading}>
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Downloading...' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Preview & Sidebar */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 items-start print:block print:w-full">
          {/* Main Invoice Column */}
          <div className="lg:col-span-2 overflow-x-auto w-full print:w-full print:p-0 print:shadow-none">
            <div className="min-w-[600px] md:min-w-0 bg-white shadow-sm rounded-lg border border-slate-100 p-1">
              <InvoicePrintView invoice={invoice} elementId="invoice-content" noShadow />
            </div>
          </div>

          {/* Sidebar Column */}
          <div className="lg:col-span-1 space-y-6 print:hidden">
            {/* Payment Configuration Card */}
            <Card className="border-slate-100 shadow-sm overflow-hidden bg-white text-left">
              <div className="p-5 border-b border-slate-50 bg-slate-50/50">
                <h3 className="font-heading font-bold text-sm text-slate-800 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-indigo-500" />
                  Payment Configuration
                  {!isPro && (
                    <Badge variant="secondary" className="ml-auto text-[9px] bg-amber-100 text-amber-800 border-amber-200 py-0 px-1.5 font-semibold">
                      PRO
                    </Badge>
                  )}
                </h3>
              </div>
              <CardContent className="p-5 space-y-4">
                {/* Stripe status */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/30">
                  <div className="space-y-0.5">
                    <p className="text-xs font-semibold text-slate-700">Stripe Payment Link</p>
                    <p className="text-[10px] text-muted-foreground">Direct online checkout</p>
                  </div>
                  {((invoice as any).from || {}).stripePaymentLink ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/10">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                        Active
                      </span>
                      <a
                        href={((invoice as any).from || {}).stripePaymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                      Inactive
                    </span>
                  )}
                </div>

                {/* PayPal status */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/30">
                  <div className="space-y-0.5">
                    <p className="text-xs font-semibold text-slate-700">PayPal Link</p>
                    <p className="text-[10px] text-muted-foreground">PayPal.me quick payments</p>
                  </div>
                  {((invoice as any).from || {}).paypalLink ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/10">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                        Active
                      </span>
                      <a
                        href={((invoice as any).from || {}).paypalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                      Inactive
                    </span>
                  )}
                </div>

                {!((invoice as any).from || {}).stripePaymentLink && !((invoice as any).from || {}).paypalLink && (
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    To enable online payments for your client, edit your Business Profile in Settings to add Stripe or PayPal links.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Client View Tracking Card */}
            {!isPro ? (
              <Card className="border-slate-100 shadow-sm overflow-hidden bg-white relative text-left">
                <div className="p-5 border-b border-slate-50 bg-slate-50/50">
                  <h3 className="font-heading font-bold text-sm text-slate-800 flex items-center gap-2">
                    <Eye className="h-4 w-4 text-indigo-500" />
                    Client View Tracking
                  </h3>
                </div>
                <CardContent className="p-6 text-center space-y-4 relative min-h-[160px]">
                  {/* Decorative Lock and Blurred Background Effect */}
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-[1.5px] flex flex-col items-center justify-center p-4 z-10">
                    <div className="bg-gradient-to-tr from-amber-500 to-amber-600 p-2.5 rounded-full text-white shadow-md mb-2 animate-bounce">
                      <Lock className="h-4 w-4" />
                    </div>
                    <h4 className="font-heading font-bold text-xs text-slate-900">Unlock Client Tracking</h4>
                    <p className="text-[10px] text-slate-500 max-w-[200px] mt-1 mb-3 leading-relaxed">
                      See exactly when clients open your invoice, what device they used, and view history.
                    </p>
                    <Link to="/subscription">
                      <Button className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm flex items-center gap-1.5 h-8 text-xs py-1.5 px-3">
                        <Crown className="h-3.5 w-3.5 text-amber-300" />
                        Upgrade to PRO
                      </Button>
                    </Link>
                  </div>
                  
                  {/* Blurred mock logs list behind the lock overlay */}
                  <div className="space-y-3 opacity-25 select-none pointer-events-none text-left">
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-slate-300" />
                      <div className="space-y-1 flex-1">
                        <div className="h-3 w-24 bg-slate-200 rounded" />
                        <div className="h-2 w-32 bg-slate-150 rounded" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-slate-300" />
                      <div className="space-y-1 flex-1">
                        <div className="h-3 w-20 bg-slate-200 rounded" />
                        <div className="h-2 w-28 bg-slate-150 rounded" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-100 shadow-sm overflow-hidden bg-white text-left">
                <div className="p-5 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center">
                  <h3 className="font-heading font-bold text-sm text-slate-800 flex items-center gap-2">
                    <Eye className="h-4 w-4 text-indigo-500" />
                    Client View Tracking
                  </h3>
                  <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 font-semibold border-indigo-100/50 text-[10px] px-1.5 py-0.5">
                    {views.length} {views.length === 1 ? 'View' : 'Views'}
                  </Badge>
                </div>
                <CardContent className="p-5">
                  {loadingViews ? (
                    <div className="flex flex-col items-center justify-center py-8 space-y-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                      <span className="text-[10px] text-muted-foreground">Loading history...</span>
                    </div>
                  ) : views.length === 0 ? (
                    <div className="text-center py-8 space-y-2 border border-dashed border-slate-100 rounded-lg">
                      <div className="mx-auto bg-slate-50 p-2 rounded-full w-fit text-slate-400">
                        <Eye className="h-5 w-5" />
                      </div>
                      <p className="text-xs text-slate-700 font-medium">No views logged yet</p>
                      <p className="text-[10px] text-muted-foreground max-w-[180px] mx-auto leading-relaxed">
                        Share the invoice link with your client to track their views.
                      </p>
                    </div>
                  ) : (
                    <div className="flow-root">
                      <ul role="list" className="-mb-8">
                        {views.map((view, viewIdx) => {
                          const OS = view.os;
                          const browser = view.browser;
                          
                          return (
                            <li key={view.id}>
                              <div className="relative pb-8">
                                {viewIdx !== views.length - 1 ? (
                                  <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-slate-100" aria-hidden="true" />
                                ) : null}
                                <div className="relative flex space-x-3 text-left">
                                  <div>
                                    <span className="h-8 w-8 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-600 shadow-sm">
                                      {OS === 'iOS' || OS === 'Android' ? (
                                        <Smartphone className="h-4 w-4 text-slate-500" />
                                      ) : (
                                        <Laptop className="h-4 w-4 text-slate-500" />
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0 pt-1.5">
                                    <div className="flex justify-between items-start gap-4">
                                      <div>
                                        <p className="text-xs font-semibold text-slate-800">
                                          Invoice Viewed
                                        </p>
                                        <p className="text-[10px] text-muted-foreground mt-0.5">
                                          {browser} on {OS}
                                        </p>
                                      </div>
                                      <div className="text-right whitespace-nowrap text-[10px] text-slate-400">
                                        <time dateTime={view.viewedAt.toISOString()}>
                                          {format(view.viewedAt, 'MMM dd, h:mm a')}
                                        </time>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
